import {
  type DiaDaSemana,
  type FaixaDeHorario,
  type Horarios,
  type TipoDeLocal,
  atualizarLocalSchema,
  criarLocalSchema,
  DIAS_DA_SEMANA,
  frota,
  horariosSchema,
  normalizarCep,
  ROTULO_DO_DIA,
  ROTULO_DO_TIPO_DE_LOCAL,
  TIPOS_DE_LOCAL,
  UNIDADES_FEDERATIVAS,
} from '@locaveri/shared'
import { Copy, DoorClosed, LoaderCircle, Plus, X } from 'lucide-react'
import { type ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { Campo } from '@/components/form/campo'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { consultarCep, digitosDoCep } from '@/lib/cep'
import { deCampoDeReaisParaWire, paraCampoDeReais } from '@/lib/dinheiro'
import { aplicarErrosDaApi, aplicarErrosDoZod, validarCom } from '@/lib/formulario'

import { type Local, useAtualizarLocal, useCriarLocal } from './api'

/**
 * Cadastro e edição de ponto de retirada e devolução.
 *
 * ---------------------------------------------------------------------------
 * DIA SEM FAIXA É FECHADO — E ISSO PRECISA ESTAR ESCRITO NA TELA
 * ---------------------------------------------------------------------------
 * A grade semanal guarda uma LISTA de faixas por dia, e lista vazia significa
 * FECHADO. O padrão silencioso erraria para o lado perigoso: quem cria um ponto
 * e não mexe nos horários pode supor "abre sempre", e a primeira retirada
 * marcada para domingo às 22h só aparece como problema no balcão. Por isso todo
 * dia vazio ganha o selo "Fechado" e uma frase dizendo o que aquilo significa —
 * nada aqui depende de o operador conhecer a convenção.
 *
 * Turno partido é DUAS faixas, não uma faixa com buraco: um ponto que fecha
 * para o almoço abre 08:00–12:00 e 13:00–18:00. É por isso que o editor tem
 * "adicionar faixa" em vez de um par de campos por dia.
 *
 * "Copiar para os demais" existe porque a alternativa é digitar catorze
 * horários iguais na mão — e digitação repetida é onde nasce o ponto que abre
 * às 8h em cinco dias e às 18h no sexto.
 *
 * ---------------------------------------------------------------------------
 * O QUE VIAJA
 * ---------------------------------------------------------------------------
 * `criarLocalSchema` TRANSFORMA (as duas taxas viram `bigint`, o CEP e a UF são
 * normalizados), então o formulário envia a ENTRADA validada — strings —, nunca
 * `validado.data`: `JSON.stringify` de um `bigint` lança. Mesmo motivo
 * documentado em `features/contas/api.ts`.
 */

const CAMPOS = [
  'nome',
  'tipo',
  'logradouro',
  'numero',
  'complemento',
  'bairro',
  'cidade',
  'uf',
  'cep',
  'latitude',
  'longitude',
  'taxaDeEntrega',
  'taxaDeDevolucao',
] as const

const LATITUDE_MAXIMA = 90
const LONGITUDE_MAXIMA = 180

/** Último minuto do dia, em `HH:MM`. Fechamento provisório da faixa recém-criada. */
const FIM_DO_DIA = '23:59'

interface FormularioDeLocal {
  nome: string
  tipo: TipoDeLocal
  logradouro: string
  numero: string
  complemento: string
  bairro: string
  cidade: string
  uf: string
  cep: string
  latitude: string
  longitude: string
  taxaDeEntrega: string
  taxaDeDevolucao: string
}

/** `01310100` → `01310-100`; aceita entrada parcial (o `formatarCep` do shared só formata o CEP completo). */
function mascararCepProgressivo(valor: string): string {
  const digitos = normalizarCep(valor).slice(0, 8)

  return digitos.length > 5 ? `${digitos.slice(0, 5)}-${digitos.slice(5)}` : digitos
}

/** Os sete dias, todos fechados. É o que o schema produz a partir de `{}`. */
function gradeVazia(): Horarios {
  return horariosSchema.parse({})
}

/** Cópia profunda da grade — o estado nunca é mutado no lugar. */
function clonarGrade(horarios: Horarios): Horarios {
  const copia = gradeVazia()

  for (const dia of DIAS_DA_SEMANA) {
    copia[dia] = horarios[dia].map((faixa) => ({ ...faixa }))
  }

  return copia
}

/**
 * O problema do dia, se houver.
 *
 * A comparação de horário é TEXTUAL e isso é seguro: `HH:MM` com zero à
 * esquerda ordena exatamente como o relógio, e é a mesma comparação que
 * `faixaDeHorarioSchema` faz no `@locaveri/shared`.
 *
 * A sobreposição é checada só aqui — o schema valida cada faixa isolada, e duas
 * faixas que se cruzam só podem ser erro de digitação: ninguém abre das 8 às 14
 * e "de novo" das 12 às 18.
 */
function problemaDoDia(faixas: readonly FaixaDeHorario[]): string | null {
  for (const faixa of faixas) {
    if (faixa.abre.length === 0 || faixa.fecha.length === 0) {
      return 'preencha a abertura e o fechamento de todas as faixas'
    }

    if (faixa.abre >= faixa.fecha) {
      return 'o fechamento precisa ser depois da abertura'
    }
  }

  const ordenadas = [...faixas].sort((primeira, segunda) =>
    primeira.abre.localeCompare(segunda.abre),
  )

  for (let indice = 1; indice < ordenadas.length; indice += 1) {
    const anterior = ordenadas[indice - 1]
    const atual = ordenadas[indice]

    if (anterior !== undefined && atual !== undefined && atual.abre < anterior.fecha) {
      return 'as faixas se sobrepõem — turno partido são faixas separadas'
    }
  }

  return null
}

function errosDaGrade(horarios: Horarios): ReadonlyMap<DiaDaSemana, string> {
  const erros = new Map<DiaDaSemana, string>()

  for (const dia of DIAS_DA_SEMANA) {
    const problema = problemaDoDia(horarios[dia])

    if (problema !== null) {
      erros.set(dia, problema)
    }
  }

  return erros
}

/**
 * A faixa que o botão "+ Faixa" propõe.
 *
 * O dia vazio nasce com um expediente inteiro. Da segunda faixa em diante o
 * padrão ENCOSTA no fechamento da anterior: um horário fixo (13:00) sobrepunha
 * a faixa de 08:00–18:00 que o próprio botão acabara de criar, e a tela acusava
 * "as faixas se sobrepõem" no clique, antes de a pessoa digitar coisa alguma.
 * Encostar é sempre válido — a checagem de sobreposição compara com `<`, não
 * `<=` — e o horário real é de quem cadastra, não do padrão.
 */
function faixaSugerida(ultima: FaixaDeHorario | undefined): FaixaDeHorario {
  if (ultima === undefined) {
    return { abre: '08:00', fecha: '18:00' }
  }

  // Sem espaço no dia (ou com a faixa anterior ainda em branco) não há sugestão
  // honesta a dar: os campos vazios já pedem o preenchimento.
  return ultima.fecha.length === 0 || ultima.fecha >= FIM_DO_DIA
    ? { abre: '', fecha: '' }
    : { abre: ultima.fecha, fecha: FIM_DO_DIA }
}

/** Coordenada em grau decimal com PONTO. Vírgula viraria `NaN` no `coerce` da API. */
function coordenadaValida(valor: string, limite: number): boolean {
  const texto = valor.trim()
  const numero = Number(texto)

  return texto.length > 0 && Number.isFinite(numero) && Math.abs(numero) <= limite
}

function valoresIniciais(local: Local | undefined): FormularioDeLocal {
  return {
    nome: local?.nome ?? '',
    tipo: local?.tipo ?? 'FILIAL',
    logradouro: local?.logradouro ?? '',
    numero: local?.numero ?? '',
    complemento: local?.complemento ?? '',
    bairro: local?.bairro ?? '',
    cidade: local?.cidade ?? '',
    uf: local?.uf ?? '',
    cep: mascararCepProgressivo(local?.cep ?? ''),
    latitude:
      local?.latitude === null || local?.latitude === undefined ? '' : String(local.latitude),
    longitude:
      local?.longitude === null || local?.longitude === undefined ? '' : String(local.longitude),
    taxaDeEntrega: paraCampoDeReais(local?.taxaDeEntrega),
    taxaDeDevolucao: paraCampoDeReais(local?.taxaDeDevolucao),
  }
}

export interface DialogoDeLocalProps {
  readonly aberto: boolean
  readonly aoFechar: () => void
  /** `undefined` cria; preenchido edita. */
  readonly local?: Local | undefined
}

/** O que a dica do CEP diz em cada momento da busca. */
const DICA_DA_BUSCA_DE_CEP = {
  OCIOSA: 'Rua, bairro, cidade e UF se preenchem pelo CEP.',
  BUSCANDO: 'Buscando o endereço…',
  NAO_ENCONTRADO: 'CEP não encontrado. Confira o número ou preencha o endereço.',
  INDISPONIVEL: 'Não deu para buscar o CEP agora. Preencha o endereço à mão.',
} as const

export function DialogoDeLocal({ aberto, aoFechar, local }: DialogoDeLocalProps): ReactElement {
  const editando = local !== undefined
  const [erroGeral, setErroGeral] = useState<string | null>(null)
  const [horarios, setHorarios] = useState<Horarios>(gradeVazia)

  const criar = useCriarLocal()
  const atualizar = useAtualizarLocal()

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    setFocus,
    reset,
    watch,
    formState: { errors, isSubmitted },
  } = useForm<FormularioDeLocal>({ defaultValues: valoresIniciais(undefined) })

  // A busca de CEP. `ultimoCepBuscado` evita repetir a consulta a cada tecla
  // depois do oitavo dígito — e descarta a resposta que chega DEPOIS de a
  // pessoa já ter trocado o CEP.
  const [buscaDoCep, setBuscaDoCep] = useState<
    'OCIOSA' | 'BUSCANDO' | 'NAO_ENCONTRADO' | 'INDISPONIVEL'
  >('OCIOSA')
  const ultimoCepBuscado = useRef<string | null>(null)

  async function preencherPeloCep(valor: string): Promise<void> {
    const digitos = digitosDoCep(valor)

    if (digitos.length !== 8) {
      ultimoCepBuscado.current = null
      setBuscaDoCep('OCIOSA')
      return
    }
    if (digitos === ultimoCepBuscado.current) return

    ultimoCepBuscado.current = digitos
    setBuscaDoCep('BUSCANDO')
    const resultado = await consultarCep(digitos)

    if (ultimoCepBuscado.current !== digitos) return
    if (resultado.situacao !== 'ACHADO') {
      setBuscaDoCep(resultado.situacao)
      return
    }

    // Rua e bairro vazios (CEP de cidade inteira) não apagam o que já foi
    // digitado: vazio do ViaCEP quer dizer "digite", não "não tem".
    const opcoes = { shouldDirty: true, shouldValidate: isSubmitted }
    const { endereco } = resultado
    if (endereco.logradouro !== '') setValue('logradouro', endereco.logradouro, opcoes)
    if (endereco.bairro !== '') setValue('bairro', endereco.bairro, opcoes)
    setValue('cidade', endereco.cidade, opcoes)
    setValue('uf', endereco.uf, opcoes)
    setBuscaDoCep('OCIOSA')
    // O número é o que o CEP nunca traz — o cursor já vai para ele.
    setFocus(endereco.logradouro === '' ? 'logradouro' : 'numero')
  }

  // O diálogo é montado uma vez e reaproveitado: sem este efeito, abrir
  // "editar" depois de "novo" mostraria os valores do formulário anterior — e a
  // grade de horários do ponto errado, que é pior, porque ninguém confere.
  useEffect(() => {
    if (aberto) {
      setErroGeral(null)
      setBuscaDoCep('OCIOSA')
      ultimoCepBuscado.current = null
      reset(valoresIniciais(local))
      setHorarios(local === undefined ? gradeVazia() : clonarGrade(local.horarios))
    }
  }, [aberto, local, reset])

  const cep = watch('cep')
  const ocupado = criar.isPending || atualizar.isPending

  const problemas = useMemo(() => errosDaGrade(horarios), [horarios])
  const diasAbertos = DIAS_DA_SEMANA.filter((dia) => horarios[dia].length > 0).length

  function adicionarFaixa(dia: DiaDaSemana): void {
    setHorarios((atual) => {
      const proximo = clonarGrade(atual)
      proximo[dia] = [...proximo[dia], faixaSugerida(proximo[dia].at(-1))]

      return proximo
    })
  }

  function removerFaixa(dia: DiaDaSemana, indice: number): void {
    setHorarios((atual) => {
      const proximo = clonarGrade(atual)
      proximo[dia] = proximo[dia].filter((_faixa, posicao) => posicao !== indice)

      return proximo
    })
  }

  function alterarFaixa(
    dia: DiaDaSemana,
    indice: number,
    campo: 'abre' | 'fecha',
    valor: string,
  ): void {
    setHorarios((atual) => {
      const proximo = clonarGrade(atual)
      proximo[dia] = proximo[dia].map((faixa, posicao) =>
        posicao === indice ? { ...faixa, [campo]: valor } : faixa,
      )

      return proximo
    })
  }

  function copiarParaOsDemais(origem: DiaDaSemana): void {
    setHorarios((atual) => {
      const proximo = clonarGrade(atual)

      for (const dia of DIAS_DA_SEMANA) {
        if (dia !== origem) {
          proximo[dia] = atual[origem].map((faixa) => ({ ...faixa }))
        }
      }

      return proximo
    })

    // A cópia SUBSTITUI os outros seis dias. Avisar é o mínimo: sem o aviso,
    // quem já tinha ajustado o sábado descobriria a perda ao salvar.
    toast.success(
      `Grade de ${ROTULO_DO_DIA[origem].toLowerCase()} copiada para os outros seis dias`,
    )
  }

  function corpo(valores: FormularioDeLocal): Record<string, unknown> {
    const complemento = valores.complemento.trim()
    const latitude = valores.latitude.trim()
    const longitude = valores.longitude.trim()

    return {
      nome: valores.nome,
      tipo: valores.tipo,
      logradouro: valores.logradouro,
      numero: valores.numero,
      // Na edição a string vazia VAI, porque é assim que se apaga o
      // complemento; na criação seria gravar vazio por gravar.
      ...(complemento.length > 0 || editando ? { complemento } : {}),
      bairro: valores.bairro,
      cidade: valores.cidade,
      uf: valores.uf,
      cep: normalizarCep(valores.cep),
      // Campo em branco vira `null` de propósito: apagar um pino errado é
      // operação legítima, e omitir o campo num PATCH significaria "não mexe".
      latitude: latitude.length > 0 ? latitude : null,
      longitude: longitude.length > 0 ? longitude : null,
      // A grade inteira substitui a anterior — não há merge por dia, senão
      // "apagar sábado" e "não mexer em sábado" seriam a mesma coisa.
      horarios,
      // Taxa em branco é ZERO explícito, não "não mexe": quem apagou o valor
      // quis dizer que o ponto não cobra. Valor malformado nem chega aqui — o
      // `validate` do campo barra antes.
      taxaDeEntrega: deCampoDeReaisParaWire(valores.taxaDeEntrega) ?? '0',
      taxaDeDevolucao: deCampoDeReaisParaWire(valores.taxaDeDevolucao) ?? '0',
    }
  }

  const aoEnviar = handleSubmit((valores) => {
    setErroGeral(null)

    if (problemas.size > 0) {
      const dias = [...problemas.keys()].map((dia) => ROTULO_DO_DIA[dia].toLowerCase()).join(', ')
      const mensagem = `Corrija o horário de ${dias} antes de salvar.`

      setErroGeral(mensagem)
      // O alerta do formulário fica no TOPO de um diálogo que rola, e o botão
      // "Salvar" está lá embaixo: sem o aviso flutuante o clique pareceria não
      // ter feito nada. Nos demais campos quem rola é o próprio react-hook-form,
      // que foca o primeiro inválido — a grade não passa por ele.
      toast.error(mensagem)
      return
    }

    const entrada = corpo(valores)
    const schema = editando ? atualizarLocalSchema : criarLocalSchema
    const validado = schema.safeParse(entrada)

    if (!validado.success) {
      setErroGeral(aplicarErrosDoZod(validado.error, setError, CAMPOS))
      return
    }

    if (local !== undefined) {
      atualizar.mutate(
        { id: local.id, dados: entrada },
        {
          onSuccess: () => {
            toast.success('Ponto atualizado')
            aoFechar()
          },
          onError: (causa: unknown) => {
            setErroGeral(aplicarErrosDaApi(causa, setError, CAMPOS))
          },
        },
      )
      return
    }

    criar.mutate(entrada, {
      onSuccess: () => {
        toast.success('Ponto cadastrado', {
          description:
            diasAbertos === 0
              ? 'Atenção: o ponto ficou fechado nos sete dias e não aparece para retirada.'
              : 'Já pode ser escolhido como base de veículo e como ponto de retirada.',
        })
        aoFechar()
      },
      onError: (causa: unknown) => {
        setErroGeral(aplicarErrosDaApi(causa, setError, CAMPOS))
      },
    })
  })

  return (
    <Dialog
      open={aberto}
      onOpenChange={(proximo) => {
        if (!proximo) {
          aoFechar()
        }
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{editando ? 'Editar ponto' : 'Novo ponto de atendimento'}</DialogTitle>
          <DialogDescription>
            Onde o cliente retira e devolve o carro. O horário cadastrado aqui é o que limita as
            janelas de retirada no app.
          </DialogDescription>
        </DialogHeader>

        <form
          noValidate
          onSubmit={(evento) => {
            void aoEnviar(evento)
          }}
          className="space-y-4"
        >
          {erroGeral === null ? null : (
            <Alert variant="danger">
              <AlertDescription>{erroGeral}</AlertDescription>
            </Alert>
          )}

          {editando && !local.ativo ? (
            <Alert variant="warning">
              <AlertDescription>
                Este ponto está desativado e não aparece no seletor de retirada. Editar aqui não o
                reativa — use “Reativar ponto” na lista.
              </AlertDescription>
            </Alert>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2">
            <Campo
              rotulo="Nome"
              obrigatorio
              erro={errors.nome?.message}
              dica="É o que aparece no seletor de retirada do app. Único dentro da locadora."
            >
              {(campo) => (
                <Input
                  {...campo}
                  {...register('nome', { required: 'informe o nome do ponto' })}
                  placeholder="Aeroporto de Congonhas"
                  disabled={ocupado}
                />
              )}
            </Campo>

            <Campo rotulo="Tipo" obrigatorio erro={errors.tipo?.message}>
              {(campo) => (
                <Select {...campo} {...register('tipo')} disabled={ocupado}>
                  {TIPOS_DE_LOCAL.map((item) => (
                    <option key={item} value={item}>
                      {ROTULO_DO_TIPO_DE_LOCAL[item]}
                    </option>
                  ))}
                </Select>
              )}
            </Campo>
          </div>

          <fieldset className="space-y-4" disabled={ocupado}>
            <legend className="text-sm font-medium text-foreground">Endereço</legend>

            <div className="grid gap-4 sm:grid-cols-6">
              <Campo
                rotulo="CEP"
                obrigatorio
                erro={errors.cep?.message}
                dica={DICA_DA_BUSCA_DE_CEP[buscaDoCep]}
                className="sm:col-span-2"
              >
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('cep', {
                      required: 'informe o CEP',
                      validate: validarCom(frota.cepSchema),
                    })}
                    value={cep}
                    onChange={(evento) => {
                      // `shouldValidate` só depois do primeiro envio: validar a
                      // cada tecla acusaria "CEP inválido" no terceiro dígito.
                      const mascarado = mascararCepProgressivo(evento.target.value)
                      setValue('cep', mascarado, { shouldValidate: isSubmitted })
                      void preencherPeloCep(mascarado)
                    }}
                    inputMode="numeric"
                    autoComplete="postal-code"
                    placeholder="00000-000"
                    className="tabular-money"
                    disabled={ocupado}
                  />
                )}
              </Campo>

              <Campo
                rotulo="Logradouro"
                obrigatorio
                erro={errors.logradouro?.message}
                className="sm:col-span-4"
              >
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('logradouro', { required: 'informe o logradouro' })}
                    autoComplete="address-line1"
                    disabled={ocupado}
                  />
                )}
              </Campo>

              <Campo
                rotulo="Número"
                obrigatorio
                erro={errors.numero?.message}
                className="sm:col-span-2"
              >
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('numero', { required: 'informe o número' })}
                    placeholder="S/N"
                    disabled={ocupado}
                  />
                )}
              </Campo>

              <Campo
                rotulo="Complemento"
                erro={errors.complemento?.message}
                className="sm:col-span-4"
              >
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('complemento')}
                    placeholder="Terminal 2, piso G1"
                    autoComplete="address-line2"
                    disabled={ocupado}
                  />
                )}
              </Campo>

              <Campo
                rotulo="Bairro"
                obrigatorio
                erro={errors.bairro?.message}
                className="sm:col-span-2"
              >
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('bairro', { required: 'informe o bairro' })}
                    disabled={ocupado}
                  />
                )}
              </Campo>

              <Campo
                rotulo="Cidade"
                obrigatorio
                erro={errors.cidade?.message}
                className="sm:col-span-2"
              >
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('cidade', { required: 'informe a cidade' })}
                    autoComplete="address-level2"
                    disabled={ocupado}
                  />
                )}
              </Campo>

              <Campo rotulo="UF" obrigatorio erro={errors.uf?.message} className="sm:col-span-2">
                {(campo) => (
                  <Select
                    {...campo}
                    {...register('uf', {
                      required: 'selecione a UF',
                      validate: validarCom(frota.ufSchema),
                    })}
                    disabled={ocupado}
                  >
                    <option value="">Selecione</option>
                    {UNIDADES_FEDERATIVAS.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </Select>
                )}
              </Campo>
            </div>
          </fieldset>

          <fieldset className="space-y-4" disabled={ocupado}>
            <legend className="text-sm font-medium text-foreground">
              Coordenadas <span className="font-normal text-fg-muted">(opcional)</span>
            </legend>
            <p className="text-xs text-fg-muted">
              Em grau decimal, com ponto. Servem para o mapa do app e para o cálculo de distância na
              entrega — sem elas o ponto continua funcionando.
            </p>

            <div className="grid gap-4 sm:grid-cols-2">
              <Campo rotulo="Latitude" erro={errors.latitude?.message}>
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('latitude', {
                      validate: (valor: string) =>
                        valor.trim().length === 0 ||
                        coordenadaValida(valor, LATITUDE_MAXIMA) ||
                        'latitude inválida — use ponto decimal, entre -90 e 90',
                    })}
                    inputMode="decimal"
                    placeholder="-23.6266"
                    className="tabular-money"
                    disabled={ocupado}
                  />
                )}
              </Campo>

              <Campo rotulo="Longitude" erro={errors.longitude?.message}>
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('longitude', {
                      validate: (valor: string) =>
                        valor.trim().length === 0 ||
                        coordenadaValida(valor, LONGITUDE_MAXIMA) ||
                        'longitude inválida — use ponto decimal, entre -180 e 180',
                    })}
                    inputMode="decimal"
                    placeholder="-46.6554"
                    className="tabular-money"
                    disabled={ocupado}
                  />
                )}
              </Campo>
            </div>
          </fieldset>

          <fieldset className="space-y-4" disabled={ocupado}>
            <legend className="text-sm font-medium text-foreground">Taxas do ponto</legend>
            <p className="text-xs text-fg-muted">
              Em reais. Deixar em branco é o mesmo que zero — o ponto não cobra.
            </p>

            <div className="grid gap-4 sm:grid-cols-2">
              <Campo rotulo="Taxa de entrega" erro={errors.taxaDeEntrega?.message}>
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('taxaDeEntrega', {
                      validate: (valor: string) =>
                        valor.trim().length === 0 ||
                        deCampoDeReaisParaWire(valor) !== null ||
                        'valor inválido — use o formato 150,00',
                    })}
                    inputMode="decimal"
                    placeholder="0,00"
                    className="tabular-money"
                    disabled={ocupado}
                  />
                )}
              </Campo>

              <Campo rotulo="Taxa de devolução" erro={errors.taxaDeDevolucao?.message}>
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('taxaDeDevolucao', {
                      validate: (valor: string) =>
                        valor.trim().length === 0 ||
                        deCampoDeReaisParaWire(valor) !== null ||
                        'valor inválido — use o formato 150,00',
                    })}
                    inputMode="decimal"
                    placeholder="0,00"
                    className="tabular-money"
                    disabled={ocupado}
                  />
                )}
              </Campo>
            </div>
          </fieldset>

          <fieldset className="space-y-3" disabled={ocupado}>
            <legend className="text-sm font-medium text-foreground">Horário de atendimento</legend>
            <p className="text-xs text-fg-muted">
              Dia sem nenhuma faixa fica <strong className="font-semibold">fechado</strong> — nunca
              “24 horas”. Quem fecha para o almoço cadastra duas faixas no mesmo dia, não uma faixa
              com buraco.
            </p>

            {diasAbertos === 0 ? (
              <Alert variant="warning">
                <AlertDescription>
                  Nenhum dia com faixa cadastrada: este ponto ficará fechado a semana inteira e não
                  aparecerá como opção de retirada.
                </AlertDescription>
              </Alert>
            ) : null}

            <div className="space-y-2">
              {DIAS_DA_SEMANA.map((dia) => {
                const faixas = horarios[dia]
                const problema = problemas.get(dia)

                return (
                  <div
                    key={dia}
                    className="space-y-2 rounded-lg border border-border bg-surface p-3"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-foreground">
                          {ROTULO_DO_DIA[dia]}
                        </span>
                        {faixas.length === 0 ? (
                          <Badge variant="neutral">
                            <DoorClosed aria-hidden="true" />
                            Fechado
                          </Badge>
                        ) : null}
                      </div>

                      <div className="flex items-center gap-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          disabled={ocupado}
                          title="Substitui a grade dos outros seis dias por esta"
                          onClick={() => {
                            copiarParaOsDemais(dia)
                          }}
                        >
                          <Copy aria-hidden="true" />
                          Copiar para os demais
                        </Button>

                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          disabled={ocupado}
                          onClick={() => {
                            adicionarFaixa(dia)
                          }}
                        >
                          <Plus aria-hidden="true" />
                          Faixa
                        </Button>
                      </div>
                    </div>

                    {faixas.length === 0 ? (
                      <p className="text-xs text-fg-muted">
                        Sem faixa cadastrada o ponto não abre neste dia.
                      </p>
                    ) : (
                      <ul className="space-y-2">
                        {faixas.map((faixa, indice) => (
                          <li
                            key={`${dia}-${String(indice)}`}
                            className="flex flex-wrap items-center gap-2"
                          >
                            <Input
                              type="time"
                              value={faixa.abre}
                              aria-label={`Abertura da ${String(indice + 1)}ª faixa de ${ROTULO_DO_DIA[dia].toLowerCase()}`}
                              onChange={(evento) => {
                                alterarFaixa(dia, indice, 'abre', evento.target.value)
                              }}
                              disabled={ocupado}
                              className="w-28 tabular-money"
                            />
                            <span className="text-xs text-fg-muted">às</span>
                            <Input
                              type="time"
                              value={faixa.fecha}
                              aria-label={`Fechamento da ${String(indice + 1)}ª faixa de ${ROTULO_DO_DIA[dia].toLowerCase()}`}
                              onChange={(evento) => {
                                alterarFaixa(dia, indice, 'fecha', evento.target.value)
                              }}
                              disabled={ocupado}
                              className="w-28 tabular-money"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="size-8"
                              disabled={ocupado}
                              aria-label={`Remover a ${String(indice + 1)}ª faixa de ${ROTULO_DO_DIA[dia].toLowerCase()}`}
                              onClick={() => {
                                removerFaixa(dia, indice)
                              }}
                            >
                              <X aria-hidden="true" />
                            </Button>
                          </li>
                        ))}
                      </ul>
                    )}

                    {problema === undefined ? null : (
                      <p className="text-xs text-danger-fg">{problema}</p>
                    )}
                  </div>
                )
              })}
            </div>
          </fieldset>

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={aoFechar} disabled={ocupado}>
              Cancelar
            </Button>
            <Button type="submit" disabled={ocupado}>
              {ocupado ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : null}
              {editando ? 'Salvar alterações' : 'Cadastrar ponto'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
